import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Wand2, Eye, X } from 'lucide-react';

import { TypeCastingBuilder } from './builders/TypeCastingBuilder';
import { StringFunctionBuilder } from './builders/StringFunctionBuilder';
import { DateTimeFunctionBuilder } from './builders/DateTimeFunctionBuilder';
import { MathFunctionBuilder } from './builders/MathFunctionBuilder';
import { CaseWhenBuilder } from './builders/CaseWhenBuilder';
import { NullHandlingBuilder } from './builders/NullHandlingBuilder';
import { WindowFunctionBuilder } from './builders/WindowFunctionBuilder';
import { JsonExtractionBuilder } from './builders/JsonExtractionBuilder';
import { CustomExpressionInput } from './builders/CustomExpressionInput';
import { TransformationPreview } from './TransformationPreview';

export type TransformationType =
  | 'none'
  | 'type_cast'
  | 'string_function'
  | 'date_function'
  | 'math_function'
  | 'case_when'
  | 'null_handling'
  | 'window_function'
  | 'json_extraction'
  | 'custom';

interface ColumnTransformationBuilderProps {
  columnName: string;
  currentTransform?: string;
  onTransformChange: (transform_expression: string | undefined) => void;
  viewId?: string;
}

export const ColumnTransformationBuilder: React.FC<ColumnTransformationBuilderProps> = ({
  columnName,
  currentTransform,
  onTransformChange,
  viewId,
}) => {
  const [transformType, setTransformType] = useState<TransformationType>('none');
  const [generatedSql, setGeneratedSql] = useState<string>(currentTransform || '');
  const [showPreview, setShowPreview] = useState(false);

  const handleTypeChange = (type: TransformationType) => {
    setTransformType(type);
    if (type === 'none') {
      setGeneratedSql('');
      onTransformChange(undefined);
    }
  };

  const handleSqlGenerated = (sql: string) => {
    setGeneratedSql(sql);
    onTransformChange(sql);
  };

  const handleClear = () => {
    setTransformType('none');
    setGeneratedSql('');
    onTransformChange(undefined);
    setShowPreview(false);
  };

  const renderBuilder = () => {
    switch (transformType) {
      case 'type_cast':
        return <TypeCastingBuilder columnName={columnName} onSqlGenerate={handleSqlGenerated} />;
      case 'string_function':
        return <StringFunctionBuilder columnName={columnName} onSqlGenerate={handleSqlGenerated} />;
      case 'date_function':
        return <DateTimeFunctionBuilder columnName={columnName} onSqlGenerate={handleSqlGenerated} />;
      case 'math_function':
        return <MathFunctionBuilder columnName={columnName} onSqlGenerate={handleSqlGenerated} />;
      case 'case_when':
        return <CaseWhenBuilder columnName={columnName} onSqlGenerate={handleSqlGenerated} />;
      case 'null_handling':
        return <NullHandlingBuilder columnName={columnName} onSqlGenerate={handleSqlGenerated} />;
      case 'window_function':
        return <WindowFunctionBuilder columnName={columnName} onSqlGenerate={handleSqlGenerated} />;
      case 'json_extraction':
        return <JsonExtractionBuilder columnName={columnName} onSqlGenerate={handleSqlGenerated} />;
      case 'custom':
        return (
          <CustomExpressionInput
            columnName={columnName}
            currentExpression={generatedSql}
            onExpressionChange={handleSqlGenerated}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Wand2 className="h-4 w-4" />
            Column Transformation
          </CardTitle>
          {transformType !== 'none' && (
            <Button variant="ghost" size="sm" onClick={handleClear}>
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Transformation Type Selector */}
        <div>
          <Label>Transformation Type</Label>
          <Select value={transformType} onValueChange={handleTypeChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select transformation type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None (Use column as-is)</SelectItem>
              <SelectItem value="type_cast">Type Casting</SelectItem>
              <SelectItem value="string_function">String Functions</SelectItem>
              <SelectItem value="date_function">Date/Time Functions</SelectItem>
              <SelectItem value="math_function">Math Functions</SelectItem>
              <SelectItem value="case_when">Conditional Logic (CASE WHEN)</SelectItem>
              <SelectItem value="null_handling">NULL Handling</SelectItem>
              <SelectItem value="window_function">Window Functions</SelectItem>
              <SelectItem value="json_extraction">JSON Extraction</SelectItem>
              <SelectItem value="custom">Custom SQL Expression</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Builder UI */}
        {transformType !== 'none' && (
          <div className="border-t pt-4">
            {renderBuilder()}
          </div>
        )}

        {/* Generated SQL Preview */}
        {generatedSql && (
          <Alert>
            <AlertDescription>
              <div className="space-y-2">
                <div className="text-sm font-medium">Generated SQL:</div>
                <code className="block p-2 bg-muted rounded text-xs whitespace-pre-wrap">
                  {generatedSql}
                </code>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Preview Button */}
        {generatedSql && viewId && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
          >
            <Eye className="h-4 w-4 mr-2" />
            {showPreview ? 'Hide' : 'Show'} Preview
          </Button>
        )}

        {/* Preview Results */}
        {showPreview && viewId && (
          <TransformationPreview
            viewId={viewId}
            columnName={columnName}
            transformation={generatedSql}
          />
        )}
      </CardContent>
    </Card>
  );
};
